import { useEffect, useMemo, useRef, useState } from 'react';
import { Gem, Award, Medal, ChevronDown, Check, Minus, MapPin, Wifi, X } from 'lucide-react';
import { calcularFidelidad, NIVELES } from '../utils/nivelFidelidad';
import '../style/NivelFidelidad.css';

const ICONOS = { plata: Medal, oro: Award, diamante: Gem };
const WHATSAPP_EDICION = `https://wa.me/5491178179204?text=${encodeURIComponent('EDICIÓN')}`;

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

function Beneficios({ items }) {
  return (
    <ul className="nf-lista">
      {items.map((b) => (
        <li key={b.texto} className={b.excluido ? 'nf-excluido' : ''}>
          {b.excluido ? <Minus size={14} /> : <Check size={14} />}
          <span>{b.texto}</span>
        </li>
      ))}
    </ul>
  );
}

// Distintivo del nivel de fidelidad de la empresa, con los beneficios en un desplegable.
function NivelFidelidad({ camaras }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);
  const f = useMemo(() => calcularFidelidad(camaras || []), [camaras]);

  useEffect(() => {
    if (!abierto) return undefined;
    const cerrar = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    const esc = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', cerrar);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', cerrar);
      document.removeEventListener('keydown', esc);
    };
  }, [abierto]);

  if (!f.cantidad) return null;

  const { nivel, proximo, zona, faltan, progreso, cantidad } = f;
  const Icono = ICONOS[nivel.id];
  const IconoProx = proximo ? ICONOS[proximo.id] : null;
  const textoFaltan = proximo
    ? `${faltan === 1 ? 'Te falta' : 'Te faltan'} ${plural(faltan, 'cámara', 'cámaras')} para ${proximo.nombre}`
    : 'Nivel máximo';
  // Lo que suma el próximo nivel: sus beneficios incluidos en la misma zona.
  const ganaProx = proximo ? proximo.beneficios[zona].filter((b) => !b.excluido) : [];

  return (
    <div className={`nf nf-${nivel.id}`} ref={ref}>
      <button
        type="button"
        className="nf-chip"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-haspopup="dialog"
      >
        <span className="nf-chip-icono"><Icono size={18} /></span>
        <span className="nf-chip-cuerpo">
          <span className="nf-chip-linea">
            <strong>Nivel {nivel.nombre}</strong>
            <span className="nf-chip-cam">· {plural(cantidad, 'cámara activa', 'cámaras activas')}</span>
          </span>
          <span className="nf-barra" aria-hidden="true"><span style={{ width: `${Math.round(progreso * 100)}%` }} /></span>
          <span className="nf-chip-faltan">{textoFaltan}</span>
        </span>
        <ChevronDown size={16} className={`nf-chev ${abierto ? 'nf-chev-up' : ''}`} />
      </button>

      {abierto && (
        <div className="nf-panel" role="dialog" aria-label={`Beneficios del nivel ${nivel.nombre}`}>
          <div className="nf-panel-head">
            <span className="nf-panel-icono"><Icono size={22} /></span>
            <div>
              <div className="nf-panel-titulo">Nivel {nivel.nombre}</div>
              <div className="nf-panel-sub">{nivel.rango} · {nivel.ritmo}</div>
            </div>
            <button type="button" className="nf-cerrar" onClick={() => setAbierto(false)} aria-label="Cerrar"><X size={16} /></button>
          </div>

          <div className="nf-zona">
            {zona === 'amba' ? <MapPin size={13} /> : <Wifi size={13} />}
            {zona === 'amba' ? 'Obras en CABA / AMBA: contenido en obra' : 'Fuera de CABA / AMBA: servicio 100% remoto'}
          </div>

          <div className="nf-escala" aria-hidden="true">
            {NIVELES.map((n) => (
              <span key={n.id} className={`nf-escala-paso ${n.id === nivel.id ? 'nf-actual' : ''} ${cantidad >= n.min ? 'nf-logrado' : ''}`}>
                <span className={`nf-punto nf-punto-${n.id}`} />
                <span className="nf-escala-nombre">{n.nombre}</span>
                <span className="nf-escala-min">{n.min === 10 ? '10+' : `${n.min}–${n.max}`}</span>
              </span>
            ))}
          </div>

          <div className="nf-seccion">Tu nivel incluye</div>
          <Beneficios items={nivel.beneficios[zona]} />

          {proximo ? (
            <div className="nf-prox">
              <div className="nf-prox-head">
                <IconoProx size={16} />
                <span>Con {plural(faltan, 'cámara más', 'cámaras más')} pasás a <strong>{proximo.nombre}</strong> ({proximo.ritmo}):</span>
              </div>
              <Beneficios items={ganaProx} />
            </div>
          ) : (
            <div className="nf-prox nf-prox-max">Estás en el nivel más alto del programa. ¡Gracias por confiar en Lisual!</div>
          )}

          <p className="nf-nota">
            El nivel se asigna solo por cámaras activas. Si sumás cámaras, subís de nivel automáticamente al mes siguiente.
          </p>
          <a className="nf-wa" href={WHATSAPP_EDICION} target="_blank" rel="noopener noreferrer">
            Para pedir tu edición, respondé EDICIÓN por WhatsApp
          </a>
        </div>
      )}
    </div>
  );
}

export default NivelFidelidad;
